import { useCookieState } from 'ahooks'
import { Button, List, Result } from 'antd'
import classnames from 'classnames'
import { useRouter } from 'next/router'
import * as React from 'react'
import { useIntl } from 'react-intl'

import { NotificationsListItem } from './NotificationsListItem'
import { NotificationState } from './NotificationsSubMenu'
import uiUrls from '@/config/uiUrls'
import { HttpMethods } from '@/config/urls'
import { accountUrls } from '@/config/urls/account'
import { useThemeContext } from '@/context/ThemeContext'
import { useWorkspaceContext } from '@/context/WorkspaceContext'
import { useMatchMutate } from '@/hooks/useMatchMutate'
import { COOKIES, httpFetch } from '@/utils/http'
import { buildI18nFunction } from '@/utils/messages'

import { AccountNotification } from '@/types/index.d'

import styles from './Notifications.module.css'

type NotificationsListProps = {
  callbacks?: {
    close?: () => void
    mount?: () => void
    unmount?: () => void
  }
  notificationState: NotificationState
  onDismissItemClick?: (notificationItem: AccountNotification) => void
  onNotificationRead?: (notificationItem: AccountNotification) => void
  onShowMoreClick: () => void
  showLoadMore: boolean
}

export function NotificationsList({
  callbacks,
  notificationState,
  onDismissItemClick,
  onNotificationRead,
  onShowMoreClick,
  showLoadMore = false,
}: NotificationsListProps) {
  const t = buildI18nFunction(useIntl().formatMessage)
  const [token] = useCookieState(COOKIES.XSRF_TOKEN)
  const matchMutate = useMatchMutate()
  const router = useRouter()
  const { workspaceId } = useWorkspaceContext()

  React.useEffect(
    () => {
      callbacks?.mount()
      return () => callbacks?.unmount()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const handleNotificationDismissClick = (
    notificationItem: AccountNotification
  ) => {
    httpFetch(
      accountUrls.getWorkspaceNotificationDismiss({
        workspaceId,
        notificationId: notificationItem.id,
      }),
      {
        method: HttpMethods.PATCH,
        xsrfToken: token,
      }
    ).then((response) => {
      if (response.ok) {
        onDismissItemClick(notificationItem)
        matchMutate(/notifications/)
      }
    })
  }

  const handleNotificationClick = (item: AccountNotification) => {
    let hrefUrl: string
    switch (item.type) {
      case 'POLICY_UPDATE':
        hrefUrl = uiUrls.settings.storeUpdatedPackages
        break

      case 'POLICY_RELEASED':
        hrefUrl = uiUrls.settings.storeNewPackages
        break
    }

    callbacks.close()
    router.push(hrefUrl)
  }

  const loadMore = showLoadMore && (
    <div className={styles.loadMore}>
      <Button
        block
        loading={notificationState.isNotificationLoading}
        onClick={onShowMoreClick}
      >
        {t('SubMenu.notifications.showMore.button')}
      </Button>
    </div>
  )

  const { isLightMode, isDarkMode } = useThemeContext()
  return (
    <List<AccountNotification>
      className={classnames(styles.notificationsList, {
        [styles.notificationsListDarkMode]: isDarkMode,
        [styles.notificationsListLightMode]: isLightMode,
      })}
      dataSource={notificationState?.notifications}
      locale={{
        emptyText: notificationState?.empty && (
          <Result
            status="success"
            subTitle={t('SubMenu.notifications.empty')}
          />
        ),
      }}
      loading={notificationState?.isNotificationLoading}
      itemLayout="vertical"
      loadMore={loadMore}
      renderItem={(item) => (
        <NotificationsListItem
          onNotificationOpenClick={handleNotificationClick}
          onNotificationDismissClick={handleNotificationDismissClick}
          onNotificationRead={onNotificationRead}
          item={item}
          workspaceId={workspaceId}
        />
      )}
    />
  )
}
